import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, UserCheck, UserX, Clock, Mail, Phone, Briefcase, Building2,
  ShieldCheck, X, Check,
} from "lucide-react";

const PERMISSION_KEYS = [
  "jobs", "adas", "estimate_analyzer", "calibration_reports", "invoices",
  "sales", "commissions", "master_data", "equipment", "work_orders",
  "reports", "users", "settings", "financials",
];

const PERM_LABELS = {
  jobs: "Jobs", adas: "ADAS Analysis", estimate_analyzer: "Estimate Analyzer",
  calibration_reports: "Calibration Reports", invoices: "Invoices",
  sales: "Sales / CRM", commissions: "Commissions", master_data: "Master Data",
  equipment: "Equipment", work_orders: "Work Orders", reports: "Reports",
  users: "Users Management", settings: "Settings", financials: "Financials",
};

const ROLE_OPTIONS = [
  { value: "technician", label: "ADAS Technician" },
  { value: "sales", label: "Sales Rep" },
  { value: "finance", label: "Finance" },
  { value: "reception", label: "Reception" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Administrator" },
];

// Role defaults — mirrors backend ROLE_DEFAULT_PERMISSIONS
const ROLE_DEFAULTS = {
  admin: PERMISSION_KEYS.reduce((a, k) => ({ ...a, [k]: true }), {}),
  manager: PERMISSION_KEYS.reduce((a, k) => ({ ...a, [k]: !["users", "financials"].includes(k) }), {}),
  technician: PERMISSION_KEYS.reduce((a, k) => ({ ...a, [k]: ["jobs", "adas", "calibration_reports", "work_orders", "equipment"].includes(k) }), {}),
  sales: PERMISSION_KEYS.reduce((a, k) => ({ ...a, [k]: ["jobs", "sales", "commissions", "estimate_analyzer", "reports"].includes(k) }), {}),
  finance: PERMISSION_KEYS.reduce((a, k) => ({ ...a, [k]: ["invoices", "commissions", "reports", "financials", "jobs"].includes(k) }), {}),
  reception: PERMISSION_KEYS.reduce((a, k) => ({ ...a, [k]: ["jobs", "sales", "master_data"].includes(k) }), {}),
};

export default function AdminPendingUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/pending-users");
      setUsers(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-amber-400 mb-2 flex items-center gap-1.5">
          <Clock className="w-3 h-3" /> ADMIN
        </div>
        <h1 className="font-display text-4xl font-semibold tracking-tight">Pending user approvals</h1>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl">
          Review new registrations from your team. Assign role + permissions, then approve or reject.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
        </div>
      ) : users.length === 0 ? (
        <Card className="bg-[#0B1120] border-slate-800 p-12 text-center">
          <UserCheck className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <h3 className="font-display text-lg text-emerald-300 mb-1">All caught up!</h3>
          <p className="text-sm text-slate-500">No pending users to review.</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="pending-users-list">
          {users.map((u) => (
            <Card key={u.id} className="bg-[#0B1120] border-slate-800 p-5" data-testid={`pending-user-${u.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-10 h-10 rounded-full bg-blue-500/15 border border-blue-500/40 flex items-center justify-center text-blue-300 font-bold text-sm">
                      {(u.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-display text-lg font-medium">{u.name}</div>
                      <div className="text-xs text-slate-500">{u.job_title || u.role} · {u.department || "—"}</div>
                    </div>
                    <Badge className="bg-amber-500/15 border-amber-500/40 text-amber-300 text-[10px] uppercase">Pending</Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-slate-400 mt-3">
                    <span className="inline-flex items-center gap-1.5"><Mail className="w-3 h-3" /> {u.email}</span>
                    {u.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3 h-3" /> {u.phone}</span>}
                    <span className="inline-flex items-center gap-1.5"><Briefcase className="w-3 h-3" /> Suggested role: <b className="text-slate-300">{u.role}</b></span>
                  </div>
                  <div className="text-[10px] text-slate-600 mt-2">
                    Invite code: <span className="font-mono text-slate-400">{u.invite_code_used}</span> · Registered {new Date(u.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button
                    onClick={() => setReviewing(u)}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
                    data-testid={`review-user-${u.id}`}
                  >
                    <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Review & Approve
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      if (!window.confirm(`Reject ${u.name}?`)) return;
                      try {
                        await api.post(`/admin/pending-users/${u.id}/reject`, { reason: "Rejected by admin" });
                        toast.success("Rejected");
                        load();
                      } catch (e) { toast.error("Reject failed"); }
                    }}
                    className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 text-xs"
                    data-testid={`reject-user-${u.id}`}
                  >
                    <UserX className="w-3.5 h-3.5 mr-1.5" /> Reject
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {reviewing && (
        <ApproveDialog
          user={reviewing}
          onClose={() => setReviewing(null)}
          onDone={() => { setReviewing(null); load(); }}
        />
      )}
    </div>
  );
}

function ApproveDialog({ user, onClose, onDone }) {
  const [role, setRole] = useState(user.role || "technician");
  const [perms, setPerms] = useState(() => ROLE_DEFAULTS[user.role] || ROLE_DEFAULTS.technician);
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // When role changes, reset perms to that role's default
    setPerms(ROLE_DEFAULTS[role] || {});
  }, [role]);

  const togglePerm = (k) => setPerms((p) => ({ ...p, [k]: !p[k] }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/pending-users/${user.id}/approve`, {
        role,
        permissions: perms,
        welcome_message: welcomeMessage.trim() || null,
      });
      toast.success(`${user.name} approved!`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B1120] border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400" /> Approve {user.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2" data-testid="approve-dialog">
          <div className="rounded-md border border-slate-800 bg-slate-900/40 p-3 grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-slate-500">Email:</span> <span className="text-slate-200">{user.email}</span></div>
            <div><span className="text-slate-500">Phone:</span> <span className="text-slate-200">{user.phone || "—"}</span></div>
            <div><span className="text-slate-500">Job:</span> <span className="text-slate-200">{user.job_title || "—"}</span></div>
            <div><span className="text-slate-500">Dept:</span> <span className="text-slate-200">{user.department || "—"}</span></div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5 block">
              Assign role (this sets a base permission template)
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="approve-role-select"
              className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            >
              {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 block">
              Fine-tune permissions (checkboxes override role defaults)
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {PERMISSION_KEYS.map((k) => (
                <label
                  key={k}
                  className={`flex items-center gap-2 rounded-md border px-2.5 py-2 cursor-pointer transition ${
                    perms[k]
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                      : "border-slate-800 bg-slate-900/40 text-slate-400 hover:border-slate-600"
                  }`}
                  data-testid={`perm-${k}`}
                >
                  <input
                    type="checkbox"
                    checked={!!perms[k]}
                    onChange={() => togglePerm(k)}
                    className="accent-emerald-500"
                  />
                  <span className="text-xs font-medium">{PERM_LABELS[k]}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5 block">
              Welcome message (optional — sent in the approval email)
            </label>
            <textarea
              value={welcomeMessage}
              onChange={(e) => setWelcomeMessage(e.target.value)}
              data-testid="approve-welcome"
              rows={2}
              placeholder="Welcome to the team! Talk to me if you have any questions."
              className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
            <Button variant="outline" onClick={onClose} className="border-slate-700" data-testid="approve-cancel">
              <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-500"
              data-testid="approve-confirm"
            >
              {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Approving...</> : <><Check className="w-3.5 h-3.5 mr-1.5" /> Approve & Send Email</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
